export default {
  slug: "half-vs-full-hyrox-simulation",
  metaTitle: "Half HYROX Simulation vs Full: Which to Book First?",
  metaDescription:
    "A half simulation covers half the race; a full sim is all 8 runs and 8 stations at official weights. Which fits where you are, and how Dallas athletes use each.",
  h1: "Half HYROX Simulation vs Full Simulation: Which Should You Book First?",
  category: "Simulations",
  pillar: false,
  datePublished: "2026-10-06",
  dateModified: "2026-10-06",
  readTime: 7,
  hero: {
    src: "/dps/bg-simulation.jpg",
    alt: "Athletes on treadmills during HYROX simulation training in Dallas",
    caption: "Half the distance still means real compromised running.",
  },
  summary:
    "A half HYROX simulation covers half the race, typically 4 runs and 4 stations or the full order at half distances. A full simulation is the complete race: 8 runs, 8 stations, official weights, timed. If your race is inside 12 weeks, book the full sim; use a half sim as a mid-block checkpoint.",
  takeaways: [
    "Half sim: roughly 30 to 60 minutes of work, a training checkpoint. Full sim: 60 to 120 minutes, a race rehearsal.",
    "Only the full simulation produces a pacing blueprint you can plan a race around.",
    "Dallas runs full simulations ($25, seeded waves); the half-sim role is covered by classes or a DIY session.",
    "The right order for most athletes: classes → half-style checkpoint → full sim → race.",
  ],
  body: [
    { t: "p", html: "The question usually hides a different one: \u201CAm I ready for the full thing?\u201D Here's what each format actually is, what each is for, and a straight answer by timeline." },

    { t: "h2", id: "half-defined", text: "What a half HYROX simulation is" },
    { t: "p", html: "Two common formats share the name. The first: half the course, usually runs 1 through 4 and stations 1 through 4, full weights. The second: the complete 8-station order with every distance halved, 500m runs, 25m sleds, half the reps. The second is the better training tool because you still touch wall balls on tired legs, which is the whole point." },

    { t: "h2", id: "full-defined", text: "What the full simulation is" },
    { t: "p", html: 'The entire race: 8 × 1km runs, all 8 stations at official division weights, timed start to finish with standards enforced. In Dallas that\'s a $25 seeded-wave event at Dallas Performance Systems; the complete picture is in <a href="/blog/what-is-a-hyrox-simulation">what a HYROX simulation is</a> and the day-of logistics are in the <a href="/simulation-format">sim format guide</a>.' },

    { t: "h2", id: "side-by-side", text: "Side by side" },
    {
      t: "table",
      head: ["", "Half simulation", "Full simulation"],
      rows: [
        ["Work time", "30–60 min", "60–120 min"],
        ["Fatigue cost", "Train again in 1–2 days", "Plan 3–5 recovery days"],
        ["Data produced", "Rough pacing feel", "Complete race blueprint: splits, transitions, weak station"],
        ["Mental test", "Partial", "The real \u201Ckilometer 6 conversation\u201D"],
        ["When in a block", "Weeks 4–8 checkpoint", "Weeks 9–12 capstone"],
        ["In Dallas", "DIY or via classes", "$25 seeded-wave event"],
      ],
    },

    { t: "h2", id: "which-first", text: "Which should you book first?" },
    {
      t: "ul",
      items: [
        "<span><strong>Race inside 12 weeks:</strong> full sim, no debate. You need the pacing data while there's still time to act on it.</span>",
        "<span><strong>New to HYROX, no race yet:</strong> a few weeks of <a href=\"/schedule\">classes</a>, then a half-style session, then the next full sim on the calendar.</span>",
        "<span><strong>Coming back from a layoff:</strong> half sim first. It tells you where you are without costing a training week.</span>",
        "<span><strong>Doubles team:</strong> go straight to the full sim. Doubles pacing is about the partner rhythm, and half the race doesn't rehearse it.</span>",
      ],
    },

    { t: "h2", id: "diy", text: "How to run a half sim yourself in Dallas" },
    { t: "p", html: "We don't stage separate half-sim events; the full sim is the measured product and classes carry the build. But a DIY half sim is easy to run honestly:" },
    {
      t: "ol",
      items: [
        "<span>Keep the official order, halve every distance: 500m runs, 500m erg and row, 25m sleds, 40m burpee broad jumps, 100m carry, 50m lunges, 38 to 50 wall balls.</span>",
        "<span>Use your division's weights. Halving distance AND load tells you nothing.</span>",
        "<span>Time it, and record splits per leg. The ratio of run time to station time is the finding.</span>",
      ],
    },
    { t: "disclosure" },
    { t: "p", html: 'A garage half sim needs surprisingly little: <a href="https://tidd.ly/45L0JwH" rel="sponsored noopener" target="_blank">kettlebells</a> at carry weight and a race-spec <a href="https://tidd.ly/4r5MH1M" rel="sponsored noopener" target="_blank">wall ball</a> cover four of the eight stations. The rest of the substitutions are on the <a href="/gear">gear page</a>.' },

    { t: "h2", id: "honest-answer", text: "The honest answer" },
    { t: "p", html: 'The half sim is training. The full sim is information. Most athletes avoid the full version because of the suffering, and that avoidance is exactly the data gap that shows up at kilometer 6 of their race. For what the full version demands leg by leg, read the <a href="/blog/hyrox-simulation-workout">station-by-station sim workout breakdown</a>.' },
  ],
  faqs: [
    { q: "Is a half simulation good for beginners?", a: "Yes, as a stepping stone. But don't park there: in Dallas, first-timers finish the full sim every cycle because waves are seeded and pacing is coached." },
    { q: "How long does a half simulation take?", a: "Most athletes land between 30 and 60 minutes, roughly half their projected full-sim time plus a little, since you never fully settle into rhythm." },
    { q: "Does Hyrox Ninja run half simulations?", a: 'Not as standalone events. Specialty classes train half-sim-style pieces weekly, and the DIY template above covers the checkpoint use case. The measured event is the <a href="/simulation-format">full simulation</a>.' },
    { q: "Can I do the full sim without ever doing a half?", a: "Yes. Seeded waves and coached standards exist precisely so the full sim can be your first. Pace the first kilometers conservatively and finish." },
  ],
  sources: [
    "HYROX official race format — hyrox.com",
    "Hyrox Ninja simulation format — hyroxninja.com/simulation-format",
  ],
  related: ["what-is-a-hyrox-simulation", "hyrox-simulation-workout", "hyrox-simulation-near-me-dallas"],
  cta: {
    heading: "Ready for the real number?",
    html: "A half sim guesses; the full sim knows. The next Dallas date, waves and spot count are one click away.",
    to: "/simulation",
    label: "See the Next Full Sim",
  },
};
